import { createContext, useContext } from "react";
import type { SiteContent } from "../content";
import { content } from "../content";
import { detectLocale, localePath as makeLocalePath, stripLocalePrefix } from "./locales";
import type { Locale } from "./locales";

interface LocaleContextValue {
  locale: Locale;
  t: SiteContent;
  localePath: (path: string) => string;
  switchLocaleUrl: () => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  children,
  locale: localeProp,
  pathname,
}: {
  children: React.ReactNode;
  // Set explicitly during build-time pre-rendering, where window doesn't exist.
  locale?: Locale;
  pathname?: string;
}) {
  const locale = localeProp ?? detectLocale();
  const t = content[locale];
  const otherLocale: Locale = locale === "lt" ? "ru" : "lt";

  const value: LocaleContextValue = {
    locale,
    t,
    localePath: (path: string) => makeLocalePath(path, locale),
    switchLocaleUrl: () => {
      const bare = stripLocalePrefix(pathname ?? window.location.pathname);
      return makeLocalePath(bare, otherLocale);
    },
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error("useLocale must be used within LocaleProvider");
  return ctx;
}
